import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Mail, Phone, GraduationCap, Briefcase, Code2 } from 'lucide-react';
import { personalInfo, education, skills } from '../data/mock';

const AboutSection = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <section id="about" className="relative py-24 bg-black">
      {/* Background gradient */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-purple-500/5 rounded-full blur-[150px]" />
      
      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-cyan-400 font-mono text-sm tracking-wider">WHO I AM</span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mt-2">About Me</h2>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left Column - Bio & Contact */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="space-y-8"
          >
            <motion.div variants={itemVariants} className="prose prose-invert">
              <p className="text-gray-400 text-lg leading-relaxed">
                {personalInfo.bio}
              </p>
              <p className="text-gray-400 text-lg leading-relaxed mt-4">
                Experienced in building scalable applications, including ERP systems and AI-driven projects. 
                With hands-on knowledge of Cisco CCNA, Kali Linux, and Agile methodologies. 
                Passionate about delivering innovative, user-focused solutions.
              </p>
            </motion.div>

            {/* Contact Info Cards */}
            <motion.div variants={itemVariants} className="grid sm:grid-cols-2 gap-4">
              <div className="p-4 bg-white/5 border border-white/10 rounded-xl hover:border-cyan-500/50 transition-all group">
                <MapPin className="text-cyan-400 mb-2" size={20} />
                <p className="text-gray-500 text-sm">Location</p>
                <p className="text-white">{personalInfo.location}</p>
              </div>
              <div className="p-4 bg-white/5 border border-white/10 rounded-xl hover:border-cyan-500/50 transition-all group">
                <Mail className="text-cyan-400 mb-2" size={20} />
                <p className="text-gray-500 text-sm">Email</p>
                <p className="text-white text-sm break-all">{personalInfo.email}</p>
              </div>
              <div className="p-4 bg-white/5 border border-white/10 rounded-xl hover:border-cyan-500/50 transition-all group">
                <Phone className="text-cyan-400 mb-2" size={20} />
                <p className="text-gray-500 text-sm">Phone</p>
                <p className="text-white">{personalInfo.phone}</p>
              </div>
              <div className="p-4 bg-white/5 border border-white/10 rounded-xl hover:border-cyan-500/50 transition-all group">
                <Briefcase className="text-cyan-400 mb-2" size={20} />
                <p className="text-gray-500 text-sm">Status</p>
                <p className="text-cyan-400">Available for Projects</p>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Column - Education & Skills */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="space-y-8"
          >
            {/* Education */}
            <motion.div variants={itemVariants}>
              <div className="flex items-center gap-2 mb-4">
                <GraduationCap className="text-cyan-400" size={24} />
                <h3 className="text-xl font-semibold text-white">Education</h3>
              </div>
              <div className="space-y-4">
                {education.map((edu, index) => (
                  <div 
                    key={index}
                    className="p-4 bg-white/5 border border-white/10 rounded-xl relative overflow-hidden group hover:border-cyan-500/30 transition-all"
                  >
                    {edu.status === 'current' && (
                      <span className="absolute top-4 right-4 px-2 py-1 bg-cyan-500/20 text-cyan-400 text-xs rounded-full">
                        Ongoing
                      </span>
                    )}
                    <h4 className="text-white font-medium">{edu.degree}</h4>
                    <p className="text-gray-400 text-sm">{edu.institution}</p>
                    <p className="text-gray-500 text-sm">{edu.period}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Skills Preview */}
            <motion.div variants={itemVariants}>
              <div className="flex items-center gap-2 mb-4">
                <Code2 className="text-cyan-400" size={24} />
                <h3 className="text-xl font-semibold text-white">Core Skills</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {[...skills.development, ...skills.devops, ...skills.security].slice(0, 6).map((skill, index) => (
                  <span
                    key={index}
                    className="px-4 py-2 bg-white/5 border border-white/10 rounded-full text-gray-300 text-sm hover:border-cyan-500/50 hover:text-cyan-400 transition-all"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
